/**
 * English is the source dictionary: every other locale is typed against it, so
 * a key added here and forgotten elsewhere is a compile error rather than a
 * blank space someone finds in production.
 *
 * Entries that take a value are functions. That keeps word order the
 * translator's decision instead of the template's.
 */
export const en = {
  common: {
    tagline: 'realtime voice translator',
    interfaceLanguage: 'Interface language',
  },

  /** The front door (task 18): the demo code, once per browser. */
  login: {
    title: 'Sign in',
    lede: 'Enter the demo access code. This browser will remember it for 12 hours.',
    submit: 'Sign in',
    checking: 'Checking…',
    wrong: 'That code is wrong. Check it and try again.',
    failed: 'Could not sign in. Try again in a moment.',
  },

  home: {
    title: 'A conversation in two languages',
    lede: 'Each of you speaks your own language and hears the other translated. Two people per room, no sign-up.',
    myLanguage: 'My language',
    peerLanguage: 'Their language',
    accessCode: 'Demo access code',
    signOut: 'Sign out',
    samePair: 'Pick two different languages — there is nothing to translate otherwise.',
    create: 'Create a room',
    creating: 'Creating…',
    createFailed: 'Could not create the room',

    readyTitle: 'The room is ready',
    youHear: (language: string) => `You hear: ${language}.`,
    theyHear: (language: string) => `They hear: ${language}.`,
    inviteLabel: 'Invitation link — send it to the other person',
    copy: 'Copy',
    copied: 'Copied',
    inviteNote:
      'The link is good for 15 minutes and lets exactly one person in. Do not post it anywhere — it is the key to the room.',
    sourceSubtitles: 'Also show the original words',
    sourceSubtitlesOn:
      'You will see what each person actually said, in their own language, next to the translation. The provider charges separately for this, so the call costs more.',
    sourceSubtitlesOff:
      'Only the translation is shown. Nothing is transcribed in the original language, and nothing extra is charged.',
    sourceSubtitlesReady: 'The original words will be shown as well.',
    goToCheck: 'Check my audio',
  },

  /**
   * The voice picker on the creation screen (task 6). Provider names are
   * brands and are not translated; only what is said about them is.
   */
  voice: {
    you: 'You',
    peer: 'The other person',
    language: 'Language',
    provider: 'Voice-over',
    openaiNote: 'built-in voice, plays straight away',
    elevenlabsNote: 'cleaner voice, starts once a phrase ends',
    noVoices: 'no voices for this language',
    voice: 'Voice',
    builtIn: 'Built-in OpenAI voice',
    loading: 'Loading voices…',
    failed: 'Could not load the voices. Try again later.',
    hintBuiltIn: 'One voice for every language, as before.',
    hintLibrary: 'A voice from the ElevenLabs library.',
    groupVerified: 'Checked for this language',
    groupOther: 'Other voices',
    accent: 'Not checked for this language, so it may sound accented.',
    noSample: 'This voice has no sample to play',
    listen: 'Listen',
    stop: 'Stop',
    needVoice: 'Pick a voice for each side set to ElevenLabs, or switch it back to OpenAI.',
  },

  join: {
    checking: 'Checking the invitation…',
    title: 'Invitation to a conversation',
    youWillHear: (language: string) => `You will hear: ${language}.`,
    theyHear: (language: string) => `They hear: ${language}.`,
    connect: 'Join',
    connecting: 'Joining…',
    full: 'The room is full.',
    failedTitle: 'Could not get in',
    createYourOwn: 'Create your own room',
    noInvite: 'There is no invitation in this link. Ask for it again.',
    unavailable: 'This room is not available',
    joinFailed: 'Could not join',
  },

  check: {
    title: 'Audio check',
    lede: 'Put headphones on: without them the translation goes back into your microphone and the other person hears an echo.',
    microphone: 'Microphone',
    defaultMic: 'Default microphone',
    level: 'Level — say something',
    applied: (features: string) => `Your browser applied: ${features}.`,
    appliedNone: 'no processing',
    echoCancellation: 'echo cancellation',
    noiseSuppression: 'noise suppression',
    autoGain: 'automatic gain',
    measureNote: 'Measure with headphones on.',
    testSound: 'Test the sound',
    testAgain: 'Test the sound again',
    start: 'Sounds good, start',
    starting: 'Connecting…',
    micDenied:
      'Microphone access was refused. Allow it in your browser settings and reload the page.',
    micNotFound: 'No microphone found. Plug one in and reload the page.',
    micFailed: 'Could not open the microphone.',
  },

  call: {
    title: 'Conversation',
    timeLeft: (time: string) => `· ${time} left`,
    waitingPeer: 'Waiting for the other person. Send them the invitation link.',
    autoplayBlocked: 'Your browser blocked audio playback.',
    enableSound: 'Turn the sound on',
    lagging:
      'The translation is falling behind. You can restart the translator — the unfinished fragment will be lost.',
    translatorStopped: (reason: string) =>
      `The translation stopped: ${reason}. The call continues — restart the translator.`,
    translationVolume: 'Translation volume',
    hearOriginal: 'Hear the original voice',
    muteMic: 'Turn the microphone off',
    unmuteMic: 'Turn the microphone on',
    restart: 'Restart the translation',
    asleep:
      'Nobody has spoken for a minute, so the translation is paused to save costs. It resumes by itself when your partner speaks.',
    wake: 'Resume now',
    leave: 'Leave',
    peerMuted: 'The other person muted their microphone',

    metricConnect: 'Translator startup',
    metricFirst: 'First translation',
    metricLag: 'Estimated lag',
    metricRtt: 'RTT to the provider',
    metricVoiceDelay: 'Voice-over delay',
    metricCost: 'Cost so far (estimate)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · credits: ${credits}`,
    costNote: "Published prices set on the server, not the providers' invoice.",
    metricVoiceLead: 'ElevenLabs vs OpenAI, average',
    voiceLeadEarlier: (seconds: string) => `${seconds} s earlier`,
    voiceLeadLater: (seconds: string) => `${seconds} s later`,
    iHear: (voice: string) => `I hear: ${voice}`,
    peerHears: (voice: string) => `They hear: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs did not answer)',

    endedTitle: 'The conversation has ended',
    endedNewRoom: 'For a new conversation, create a new room.',
    newRoom: 'New room',
    rateTitle: 'Rate the conversation',
  },

  closed: {
    'peer-left': 'The other person ended the conversation.',
    'time-limit': 'The demo time limit was reached.',
    'peer-lost': 'The other person was gone for too long.',
    'server-shutdown': 'The server is restarting.',
    expired: 'The room timed out.',
    unknown: 'The connection was closed.',
    signalLost: 'Lost contact with the server. Create a new room.',
  },

  status: {
    serverConnecting: 'Server: connecting',
    serverOpen: 'Server: connected',
    serverReconnecting: 'Server: reconnecting',
    serverClosed: 'Server: disconnected',

    peerWaiting: 'Them: waiting',
    peerConnecting: 'Them: connecting',
    peerConnected: 'Them: connected',
    peerReconnecting: 'Them: recovering',
    peerFailed: 'Them: no connection',
    peerGone: 'Them: left',

    translatorIdle: 'Translation for me: not started',
    translatorConnecting: 'Translation for me: starting',
    translatorReady: 'Translation for me: running',
    translatorReconnecting: 'Translation for me: recovering',
    translatorFailed: 'Translation for me: failed',
    translatorClosed: 'Translation for me: stopped',
    translatorAsleep: 'Translation for me: paused',

    peerTranslatorReady: 'Translation for them: running',
    peerTranslatorWaiting: 'Translation for them: waiting',
    peerTranslatorAsleep: 'Translation for them: paused',
    relayed: 'Route: through TURN',
  },

  subtitles: {
    them: 'Them',
    you: 'You',
    themEmpty: 'They have not said anything yet',
    youEmpty: 'Say something — your words and their translation appear here',
  },

  rating: {
    accuracy: 'Translation accuracy',
    voice: 'How natural the voice sounds',
    comfort: 'How comfortable the conversation was',
    send: 'Send the ratings',
    thanks: 'Thank you — the ratings are recorded.',
  },

  errors: {
    no_credit: 'the OpenAI account is out of credit — top it up',
    rate_limited: 'the translation service is rate limiting us — try again in a minute',
    unconfigured: 'the server has no translation key configured',
    unreachable: 'the server could not reach the translation service',
    offline: 'could not reach the server — check your connection',
    session_expired: 'the translation session expired — restart the translation',
    room_closed: 'this room is already closed',
    bad_offer: 'the browser produced an invalid connection offer',
    provider_refused: (status: number) =>
      `the translation service refused the connection (${status})`,
    unknown: 'could not start the translation',
  },

  banner: {
    providerUnconfigured:
      'No translation provider key is configured on the server. You can create a room, but nothing will be translated.',
  },
};

// No `as const`: the literal types would make every other locale's strings a
// type error for the crime of not being the English ones. Widened, the shape
// still has to match exactly, which is the part worth enforcing.
export type Dictionary = typeof en;
